import { EventEmitter, type ICancelListener, type IListener } from '../classes';
import debounce from 'lodash/debounce';
import {
    type IItem,
    type IResourceParams,
    type ICollectionParams,
    type IFilter,
    type IList,
    Events,
    ActionTypes,
    type IResourceEvent,
    type IPagination,
    type ISorting,
    type IBaseDataProvider
} from './models';

let id = 0;

abstract class AbstractResource<T extends IItem> extends EventEmitter {
    private readonly id: number;
    private debounced: () => Promise<void>;
    private unsubscribe: ICancelListener[];
    private readonly cancelDataProvideSubscribe: ICancelListener;
    protected path: string;
    protected resourceParams: IResourceParams;
    protected provider: IBaseDataProvider;
    protected cancelHttp: ICancelListener | null;
    protected debug: boolean;

    protected constructor(provider: IBaseDataProvider, params: object, path: string, debug: boolean = false) {
        super();
        this.debug = !!debug;
        this.id = id++;
        this.path = path;
        this.unsubscribe = [];
        this.resourceParams = params;
        this.provider = provider;
        this.cancelHttp = null;
        this.cancelDataProvideSubscribe = this.subscribeToDataProvider();
    }

    /**
     * @return {string|number|null}
     */
    public getParameter(key: string) {
        return (this.resourceParams && this.resourceParams[key]) || null;
    }

    /**
     * Subscribe on loading start
     * @param callback
     * @return {Function} unsubscribe callback
     */
    public onStart(callback: IListener) {
        return this.subscribe(Events.Started, callback);
    }

    /**
     * Subscribe on data changes
     * @param callback
     * @return {Function} unsubscribe callback
     */
    public onUpdated(callback: IListener) {
        return this.subscribe(Events.Updated, callback);
    }

    /**
     * Subscribe on remove
     * @param callback
     * @return {Function} unsubscribe callback
     */
    public onRemoved(callback: IListener) {
        return this.subscribe(Events.Removed, callback);
    }

    /**
     * Subscribe on loading errors
     * @param callback
     * @return {Function} unsubscribe callback
     */
    public onFail(callback: IListener) {
        return this.subscribe(Events.Fail, callback);
    }

    /**
     * Subscribe on loaded data
     * @param {Function} callback
     * @return {Function} unsubscribe callback
     */
    public onLoaded(callback: IListener) {
        return this.subscribe(Events.Loaded, callback);
    }

    public reload(isDebounce = false): Promise<void> {
        return isDebounce ? this.debounce() : this.load();
    }

    /**
     * Destroy all subscriptions
     */
    public close() {
        this.unsubscribe.forEach(function (call) {
            call();
        });
        this.unsubscribe = [];
        this.cancelDataProvideSubscribe();
    }

    protected abstract load(): Promise<void>;

    protected preLoad(): Promise<void> {
        if (this.cancelHttp) {
            this.cancelHttp();
        } else {
            this.notify(Events.Started);
        }
        return new Promise<void>(resolve => {
            this.cancelHttp = () => {
                this.log('cancel resolved...');
                resolve();
            };
        });
    }

    protected onLoadFailed(error: any) {
        this.log('onLoadFailed', error);
        if (error.instanceName !== 'CancelRequestError') {
            this.cancelHttp = null;
            this.notify(Events.Fail, error);
        }
    }

    protected log(...args: string[]) {
        if (this.debug) {
            console.group(`RESOURCE#${this.id} ${this.path}:`);
            console.log(new Date());
            for (let i = 0; i < args.length; i++) {
                console.log(args[i]);
            }
            console.groupEnd();
        }
    }

    protected notify(event: string, data: any = null) {
        this.emit(event, data);
    }

    protected subscribeToDataProvider() {
        return this.provider.on(Events.Updated, (event: IResourceEvent) => {
            this.log('Notified', event.forResource, this.path, this.path === event.forResource ? 'true' : 'false');
            if (this.path === event.forResource) {
                this.notify(Events.Updated);
            }
        });
    }

    private subscribe(event: string, callback: IListener): () => void {
        if (event && callback) {
            const cancel = this.on(event, callback);
            this.unsubscribe.push(cancel);
            return () => this.removeSubscription(cancel);
        }
        return null;
    }

    private removeSubscription(cancel: ICancelListener) {
        const index = this.unsubscribe.findIndex(cancel);
        if (-1 !== index) {
            cancel();
            this.unsubscribe.splice(index, 1);
        }
    }

    private get debounce(): () => Promise<void> {
        if (!this.debounced) {
            this.debounced = debounce(this.load.bind(this), 300, { leading: false });
        }
        return this.debounced;
    }
}

export class CollectionResource<T extends IItem> extends AbstractResource<T> {
    private collectionParams: ICollectionParams;
    private fnCollectionParamsTransformer: (paramsIn: ICollectionParams) => ICollectionParams | null;
    private fetchAllItems = false;

    constructor(provider: IBaseDataProvider, params: IResourceParams, path: string, debug: boolean = false) {
        super(provider, params, path, debug);
        this.fnCollectionParamsTransformer = null;
        this.collectionParams = {};
    }

    public linkCollectionParams(params: ICollectionParams = {}) {
        this.collectionParams = params;
    }

    public setFetchAllItems(fetchAllItems: boolean): void {
        this.fetchAllItems = fetchAllItems;
    }

    public addFilterParams(params: IFilter) {
        if (!this.collectionParams.filters) {
            this.collectionParams.filters = params;
        } else {
            Object.assign(this.collectionParams.filters, params);
        }
    }

    public addPaginationParam(params: IPagination) {
        if (!this.collectionParams.pagination) {
            this.collectionParams.pagination = params;
        } else {
            Object.assign(this.collectionParams.pagination, params);
        }
    }

    public addSortingParams(params: ISorting[]) {
        if (!this.collectionParams.sorting) {
            this.collectionParams.sorting = params;
        } else {
            Object.assign(this.collectionParams.sorting, params);
        }
    }

    public setSortingParams(params: ISorting[]) {
        this.collectionParams.sorting = params;
    }

    public setCollectionParamsTransformer(callback: (paramsIn: ICollectionParams) => ICollectionParams | null): void {
        this.fnCollectionParamsTransformer = callback;
    }

    public close() {
        delete this.collectionParams;
        delete this.fnCollectionParamsTransformer;
        super.close();
    }

    public getCollection(cancelPromise: Promise<void> = null): Promise<IList<T>> {
        let collectionParams = this.collectionParams;
        if (typeof this.fnCollectionParamsTransformer == 'function') {
            collectionParams = this.fnCollectionParamsTransformer(collectionParams);
        }
        const providerAll = (
            this.provider as {
                all?: (collectionParams?: ICollectionParams, resourceParams?: IResourceParams, cancel?: Promise<void>) => Promise<T[]>;
            }
        ).all;
        if (this.fetchAllItems && typeof providerAll === 'function') {
            return providerAll.call(this.provider, collectionParams, this.resourceParams, cancelPromise).then(
                (items: T[]): IList<T> => ({
                    items,
                    page: {
                        number: 0,
                        size: items.length,
                        totalElements: items.length,
                        totalPages: 1
                    }
                })
            );
        }
        return this.provider.list(collectionParams, this.resourceParams, cancelPromise);
    }

    public add(item: T): Promise<T> {
        return this.provider.add(item, this.resourceParams);
    }

    public remove(id: number): Promise<void> {
        const params = Object.assign({ id: id }, this.resourceParams);
        return this.provider.remove(params);
    }

    protected load(): Promise<void> {
        const cancelPromise = this.preLoad();
        return this.getCollection(cancelPromise)
            .then(data => {
                this.cancelHttp = null;
                const items = data && data.items ? data.items : [];
                if (this.collectionParams.pagination && data && data.page) {
                    const pagination = this.collectionParams.pagination;
                    pagination.totalElements = data.page.totalElements || 0;
                    pagination.totalPages = data.page.totalPages || 0;
                }
                this.notify(Events.Loaded, items);
            })
            .catch(error => {
                this.onLoadFailed(error);
            });
    }
}

export class ItemResource<T extends IItem> extends AbstractResource<T> {
    constructor(provider: IBaseDataProvider, params: IResourceParams, path: string) {
        super(provider, params, path);
    }

    public getItem(cancelPromise: Promise<void> = null): Promise<T> {
        return this.provider.get(this.resourceParams, {}, cancelPromise);
    }

    public update(item: T): Promise<T> {
        return this.provider.update(item, this.resourceParams);
    }

    public remove(): Promise<void> {
        return this.provider.remove(this.resourceParams);
    }

    protected load() {
        const cancelPromise = this.preLoad();
        return this.getItem(cancelPromise)
            .then(data => {
                this.cancelHttp = null;
                this.notify(Events.Loaded, data);
            })
            .catch(error => {
                this.onLoadFailed(error);
            });
    }

    protected subscribeToDataProvider() {
        return this.provider.on(Events.Updated, (event: IResourceEvent) => {
            this.log(
                'Notified',
                event.forResource,
                this.path,
                this.path === event.forResource ? 'true' : 'false',
                this.path === event.action.hash ? 'true' : 'false'
            );
            if (this.path === event.action.hash && event.action.type === ActionTypes.Remove) {
                this.notify(Events.Removed);
            } else if (this.path === event.forResource) {
                this.notify(Events.Updated);
            }
        });
    }
}

export abstract class Resource extends AbstractResource<IItem> {}
